import Api360Service from '../services/360.service.js';

const REFUNDS_FETCH_LIMIT = 200;

export const dataMixin = {
  methods: {
    async fetchUserData() {
      this.loading = true;
      this.error = null;
      this.showOverlay = true;
      
      try {
        const token = this._getToken();
        const userId = this._getUserId();
        
        this.userData = await Api360Service.getUserData(userId, token);
        this.userInfo = this.buildUserInfo(this.userData);
        this.error = null;
      } catch (error) {
        this.error = error;
        this.userData = null;
        this.userInfo = null;
      } finally {
        this.loading = false;
        this.showOverlay = false;
      }
    },

    async fetchPetData() {
      try {
        const token = this._getToken();
        const userId = this._getUserId();
        
        const petData = await Api360Service.getPetData(userId, token);
        this.petInfo = petData.map(pet => ({
          id: pet.id,
          mascota: pet.name,
          species: this.normalizeSpecies(pet.species),
          sex: pet.sex,
          sexCode: pet.sexCode,
          edad: pet.age,
          raza: pet.breed,
          breedId: pet.breedId,
          indicador: pet.protectionLevel,
          birthdate: pet.birthdate,
          microchip: pet.microchip,
          hasMicrochip: pet.hasMicrochip,
          isPurebred: pet.isPurebred,
          visible: pet.visible
        }));
      } catch (error) {
        this.petInfo = [];
        throw error;
      }
    },

    async fetchMembershipData() {
      try {
        const token = this._getToken();
        const userId = this._getUserId();
        
        const membershipData = await Api360Service.getMembershipData(userId, token);
        this.detailsPlan = membershipData.map(membership => ({
          id: membership.id,
          petId: membership.petId,
          name: membership.petName,
          ref: membership.ref,
          species: membership.petSpecies,
          planName: membership.planName,
          startdate: membership.startDate,
          expirationDate: membership.expirationDate || '-',
          endDate: membership.endDate || '-',
          paymentInterval: membership.paymentInterval,
          status: membership.status,
          // true si el plan es de pago elegible para link MP (B2C puro o contrato b2c/b2b2c)
          isPaidPlan: membership.isPaidPlan,
          contractUrl: membership.contractUrl,
          // Empresa/contrato asociados (null cuando es membresía directa B2C)
          companyName: membership.companyName || null,
          contratoCodigo: membership.contratoCodigo || null,
          // Canal de adquisición: de dónde proviene la membresía
          acquisitionChannel: membership.acquisitionChannel || '-'
        }));
      } catch (error) {
        this.detailsPlan = [];
        throw error;
      }
    },

    // Trae todos los reembolsos en una llamada: la tabla "Uso de coberturas" los mezcla con los
    // demás usos y pagina en el cliente. 200 cubre de sobra al usuario con más reembolsos (hoy 42).
    async fetchRefundsData() {
      this.refundsLoadFailed = false;
      try {
        const token = this._getToken();
        const userId = this._getUserId();

        const { data, total } = await Api360Service.getRefundsData(userId, token, 1, REFUNDS_FETCH_LIMIT);
        this.requestUsage = data.map(refund => ({
          // Membresía del reembolso: la pestaña «Coberturas y servicios» filtra por ella.
          membershipId: refund.membershipId || null,
          name: refund.name,
          species: refund.specie,
          service: refund.service,
          referenceIndex: refund.referenceIndex,
          ces: refund.ces,
          createdAt: refund.createdAt,
          statusChangedAt: refund.statusChangedAt,
          changelog: refund.changelog,
          // The backend already formats this amount with the user's country currency.
          refundedAmount: refund.refundedAmount
        }));
        this.refundsTotal = total || 0;
      } catch (error) {
        this.requestUsage = [];
        this.refundsTotal = 0;
        this.refundsLoadFailed = true;
        throw error;
      }
    },

    // Usos de cobertura que no son reembolso. Si falla, el historial de reembolsos sigue
    // visible y la tabla avisa que no pudo cargar los demás usos.
    async fetchServiceUsage() {
      this.usageLoadFailed = false;
      try {
        const token = this._getToken();
        const userId = this._getUserId();

        const usages = await Api360Service.getServiceUsage(userId, token);
        this.serviceUsage = usages.map(usage => ({
          id: usage.id,
          membershipId: usage.membershipId || null,
          // null = uso sin tipo: la tabla no dibuja chip
          origin: usage.origin,
          usedDate: usage.usedDate,
          name: usage.petName,
          species: usage.petSpecies,
          service: usage.serviceName,
          // Ya viene formateado con la moneda del usuario; null = no disponible, distinto de cero.
          refundedAmount: usage.coveredAmount
        }));
      } catch (error) {
        this.serviceUsage = [];
        this.usageLoadFailed = true;
        throw error;
      }
    },

    async fetchSponsorship() {
      try {
        const token = this._getToken();
        const userId = this._getUserId();
        const sponsorshipData = await Api360Service.getSponsorshipData(userId, token);
        this.sponsorship = sponsorshipData.map(sponsor => ({
          id: sponsor.id,
          userId: sponsor.userId,
          name: sponsor.name,
          status: sponsor.status,
          organization: sponsor.organization,
          plansName: sponsor.plan,
          startDate: sponsor.startDate,
          endDate: sponsor.endDate,
          nextPaymentDate: sponsor.nextPaymentDate
        }));
      } catch (error) {
        this.sponsorship = [];
        throw error;
      }
    },

    async fetchMembershipServicesData() {
      try {
        const token = this._getToken();
        const userId = this._getUserId();
        
        const servicesData = await Api360Service.getMembershipServicesData(userId, token);
        this.stats = this.buildStatsObject(servicesData);
      } catch (error) {
        this.stats = {};
        throw error;
      }
    },

    buildUserInfo(userData) {
      return {
        userId: this.$route.params.pgId,
        name: userData.name,
        rut: userData.identifier,
        email: userData.email,
        phone: userData.phone,
        birthdate: userData.birthdate,
        address: userData.address,
        country: userData.country,
        benefit: userData.benefit,
        profileImg: userData.profileImg || '/img/default-user.png',
        lastAccess: userData.lastAccess || '-',
        // Fechas completas (ISO) para la tarjeta «Accesos» de Perfil y seguridad.
        lastAccessAt: userData.lastAccessAt || null,
        accountCreatedAt: userData.accountCreatedAt || null,
        // Dispositivos con push token por plataforma: [{ platform, count }].
        devices: userData.devices || [],
        state: userData.state,
        note: userData.note || '',
        isVip: userData.isVip || false,
        coTutors: userData.coTutors || [],
        identityValidatedAt: userData.identityValidatedAt || null,
      };
    },

    buildStatsObject(servicesData) {
      const stats = {};
      servicesData.forEach(stat => {
        const petName = stat.pet || stat.petName;
        if (petName) {
          stats[petName] = {
            servicioMasUsado: stat.mostUsedService || 'N/A',
            numeroReembolsos: stat.refundsAmount || 0,
            montoReembolsos: stat.refundsApplied || 0,
            ingresosFecha: stat.incomes || 0,
            siniestralidad: stat.rate || 0
          };
        }
      });
      return stats;
    }
  }
};